import { useCallback, useMemo } from 'react'
import { reject } from 'lodash-es'
import { useTheme } from '@mui/material/styles'
import { useAppStore } from '@stores'
import { useControlStore, useModelStore, useSceneStore } from '@editorStores'
import {
  useElementIdToAssembly,
  useElementLabel,
  useFloorplanViewActive,
  useGuidToCrossSection,
  useHiddenElements,
} from '@editorHooks'
import { beamThickness } from '../ModelMesh/constants'
import {
  COLUMN_TEXT_DEPTH_DELTA,
  FLAT_TEXT_DEPTH,
  LABEL_POSITION_OFFSET,
  LABEL_SCALE,
  LABEL_SCALE_SINGLE_LINE,
  MEMBER_LABEL_FONT_SIZE_INSET,
  MEMBER_TEXT_DEPTH_DELTA,
  SLAB_BORDER_WIDTH,
  SLAB_TEXT_DEPTH,
  TEXT_3D_GLYPH_SIZE_FACTOR,
  VERTICAL_SLAB_LABEL_OFFSET,
  WALL_BORDER_WIDTH,
} from './constants'
import { ElementLabelDescriptor } from './types'
import {
  getColumnLabelPlacement,
  getMemberLabelPlacement,
  getVerticalSlabLabelPlacement,
  getWallLabelPlacement,
  makeAssemblyLine,
  makeMemberLabelLine,
} from './utils'

export type { ElementLabelDescriptor }

/**
 * Collects the labels of all model elements (beams, purlins, columns, walls and
 * vertical (roof) slabs) that are currently supposed to be visible.
 */
const useElementLabelDescriptors = (): ElementLabelDescriptor[] => {
  const { scenePalette } = useTheme()

  const model = useModelStore(state => state.model)
  const invisible = useFloorplanViewActive()
  const translucent = useModelStore(state => state.translucent)
  const labelVisibilityByType = useModelStore(state => state.labelVisibilityModel.visibilityByType)
  const transparencyVisibilityByType = useModelStore(
    state => state.transparencyVisibilityModel.visibilityByType,
  )
  const guidToTransparent = useModelStore(
    state => state.transparencyVisibilityModel.guidToTransparent,
  )
  const showLabels = useControlStore(state => state.showLabels)
  const guidToColor = useSceneStore(state => state.guidToColor)
  const transparentOpacity = useAppStore(state => state.transparentOpacityV2 ?? 0.1)

  const hiddenElements = useHiddenElements()
  const guidToCrossSection = useGuidToCrossSection()
  const getElementLabel = useElementLabel()
  const elementIdToAssembly = useElementIdToAssembly()

  const showLabelsForType = useCallback(
    (elementType?: ElementTypes) =>
      !!elementType && showLabels && labelVisibilityByType[elementType],
    [labelVisibilityByType, showLabels],
  )

  const getTransparencyOpacity = useCallback(
    (guid: string, elementType?: ElementTypes) => {
      if (!translucent) return 1

      // Priority: guidToTransparent has higher priority than visibilityByType
      const isTransparentForGuid = guidToTransparent[guid]
      if (isTransparentForGuid !== undefined) {
        // Use fixed opacity values: transparentOpacity for transparent, 1 for notTransparent
        return isTransparentForGuid ? transparentOpacity : 1
      }

      // Fallback to type-based transparency
      if (!elementType) return 1
      const isTransparentForType = transparencyVisibilityByType[elementType]
      return isTransparentForType ? transparentOpacity : 1
    },
    [translucent, transparencyVisibilityByType, guidToTransparent, transparentOpacity],
  )

  const memberDescriptors = useMemo(() => {
    if (invisible) return []

    return [...model.beams, ...model.columns, ...model.purlins].reduce(
      (collector: ElementLabelDescriptor[], element) => {
        const {
          guid,
          element_type: type,
          shape: { start, end },
        } = element

        if (hiddenElements.has(guid) || !showLabelsForType(type as ElementTypes)) return collector

        const crossSection = guidToCrossSection[guid]
        const csHeight = crossSection?.shape?.height || beamThickness
        const csWidth = crossSection?.shape?.width || beamThickness

        const isColumn = type === 'columns'
        const { position, xDirection, yDirection } = isColumn
          ? getColumnLabelPlacement(start, end, csWidth)
          : getMemberLabelPlacement(start, end, csHeight)

        collector.push({
          guid,
          textLines: [makeMemberLabelLine(getElementLabel(guid), crossSection)],
          position,
          xDirection,
          yDirection,
          fontSize:
            ((isColumn ? csHeight : csWidth) - MEMBER_LABEL_FONT_SIZE_INSET) *
            TEXT_3D_GLYPH_SIZE_FACTOR,
          color: 'black',
          opacity: getTransparencyOpacity(guid, type as ElementTypes),
          textDepth: isColumn ? COLUMN_TEXT_DEPTH_DELTA : csHeight + MEMBER_TEXT_DEPTH_DELTA * 2,
          extrudedThroughElement: !isColumn,
        })

        return collector
      },
      [],
    )
  }, [
    invisible,
    model.beams,
    model.columns,
    model.purlins,
    hiddenElements,
    showLabelsForType,
    guidToCrossSection,
    getElementLabel,
    getTransparencyOpacity,
  ])

  const shapeDescriptors = useMemo(() => {
    if (invisible) return []

    return [...model.walls, ...model.vertical_slabs, ...model.vertical_roof_slabs].reduce(
      (collector: ElementLabelDescriptor[], element) => {
        const {
          guid,
          element_type: type,
          shape: { points },
        } = element

        if (hiddenElements.has(guid) || !showLabelsForType(type as ElementTypes)) return collector
        // Don't label shapes with < 3 points as they are not rendered either
        if (points.length < 3) return collector

        const opacity = getTransparencyOpacity(guid, type as ElementTypes)
        const elementLabel = getElementLabel(guid)

        if (type === 'outer_walls' || type === 'inner_walls') {
          const color =
            guidToColor[guid] ||
            (type === 'outer_walls'
              ? (scenePalette.elements2d.externalWalls.color as string)
              : (scenePalette.elements2d.internalWalls.color as string))

          collector.push({
            guid,
            textLines: reject(
              [elementLabel, elementIdToAssembly[guid]?.short_name || ''],
              s => s === '',
            ),
            ...getWallLabelPlacement(points),
            fontSize: LABEL_SCALE * TEXT_3D_GLYPH_SIZE_FACTOR,
            color,
            opacity,
            textDepth: FLAT_TEXT_DEPTH,
            backgroundColor: 'white',
            borderColor: color,
            borderWidth: WALL_BORDER_WIDTH,
          })

          return collector
        }

        if (type !== 'vertical_slabs' && type !== 'vertical_roof_slabs') return collector

        const assembly = elementIdToAssembly[guid]
        if (!assembly) return collector

        const isRoofSlab = type === 'vertical_roof_slabs'
        const color = scenePalette.elements2d[type].outlineColor as string

        collector.push({
          guid,
          textLines: reject([elementLabel, makeAssemblyLine(assembly)], s => s === ''),
          ...getVerticalSlabLabelPlacement(
            points,
            isRoofSlab ? LABEL_POSITION_OFFSET : VERTICAL_SLAB_LABEL_OFFSET,
          ),
          fontSize: LABEL_SCALE_SINGLE_LINE * TEXT_3D_GLYPH_SIZE_FACTOR,
          color,
          opacity,
          textDepth: SLAB_TEXT_DEPTH,
          backgroundColor: 'white',
          // roof slab labels intentionally render without a border
          borderColor: isRoofSlab ? undefined : color,
          borderWidth: SLAB_BORDER_WIDTH,
        })

        return collector
      },
      [],
    )
  }, [
    invisible,
    model.walls,
    model.vertical_slabs,
    model.vertical_roof_slabs,
    hiddenElements,
    showLabelsForType,
    getTransparencyOpacity,
    getElementLabel,
    elementIdToAssembly,
    guidToColor,
    scenePalette.elements2d,
  ])

  return useMemo(
    () => [...memberDescriptors, ...shapeDescriptors],
    [memberDescriptors, shapeDescriptors],
  )
}

export default useElementLabelDescriptors
